/**
 * Home: the latest published page posts as compact rows — page, time, text,
 * a thumbnail, and their numbers. A row opens that post in the Posts screen.
 */
import React from 'react';
import { View, Text, Image, TouchableOpacity, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { useLocale } from '../../contexts/LocaleContext';
import { useRTLStyles } from '../../hooks/useRTLStyles';
import { DirectionalIcon } from '../DirectionalIcon';
import type { RecentPost } from '../../screens/dashboard/RecentPostsCard';

interface Props {
  posts: RecentPost[];
  onOpen: (post: RecentPost) => void;
  onViewAll: () => void;
}

const compactNumber = (n?: number) => {
  const v = n || 0;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return String(v);
};

export const PagePostsList: React.FC<Props> = ({ posts, onOpen, onViewAll }) => {
  const styles = useThemedStyles(getStyles);
  const { t, locale } = useLocale();
  const rtl = useRTLStyles();

  if (posts.length === 0) {
    return (
      <View style={[styles.card, styles.empty]}>
        <Ionicons name="newspaper-outline" size={22} color={colors.text.secondary as string} />
        <Text style={styles.emptyText}>{t('home.posts.empty')}</Text>
      </View>
    );
  }

  return (
    <View style={styles.card}>
      {posts.map((post, index) => {
        const thumb = post.mediaType === 'photo' ? post.mediaUrls?.[0] : undefined;
        const when = post.publishedAt || post.createdAt;
        return (
          <TouchableOpacity
            key={post.postId}
            style={[styles.row, { flexDirection: rtl.rowDirection }, index > 0 && styles.divider]}
            onPress={() => onOpen(post)}
            activeOpacity={0.8}
          >
            {post.pageAvatar ? (
              <Image source={{ uri: post.pageAvatar }} style={styles.avatar} />
            ) : (
              <View style={[styles.avatar, styles.avatarFallback]}>
                <Ionicons name="logo-facebook" size={16} color="#1877F2" />
              </View>
            )}
            <View style={styles.body}>
              <Text style={[styles.meta, { textAlign: rtl.textStart }]} numberOfLines={1}>
                {post.pageName || t('posts.facebookPage')} · {new Date(when).toLocaleDateString(locale, { day: 'numeric', month: 'short' })}
              </Text>
              <Text style={[styles.text, { textAlign: rtl.textStart }]} numberOfLines={2}>
                {post.message || (post.mediaType === 'video' ? t('posts.videoPost') : post.mediaType === 'photo' ? t('posts.photoPost') : post.linkUrl || '')}
              </Text>
              <View style={[styles.stats, { flexDirection: rtl.rowDirection }]}>
                <Ionicons name="eye-outline" size={12} color={colors.text.secondary as string} />
                <Text style={styles.stat}>{compactNumber(post.insights?.reach)}</Text>
                <Ionicons name="heart-outline" size={12} color={colors.text.secondary as string} />
                <Text style={styles.stat}>{compactNumber(post.insights?.reactions)}</Text>
                <Ionicons name="chatbubble-outline" size={12} color={colors.text.secondary as string} />
                <Text style={styles.stat}>{compactNumber(post.insights?.comments)}</Text>
              </View>
            </View>
            {thumb ? <Image source={{ uri: thumb }} style={styles.thumb} /> : post.mediaType === 'video' ? (
              <View style={[styles.thumb, styles.videoThumb]}><Ionicons name="play" size={16} color="#FFFFFF" /></View>
            ) : null}
          </TouchableOpacity>
        );
      })}
      <TouchableOpacity style={[styles.viewAll, { flexDirection: rtl.rowDirection }]} onPress={onViewAll}>
        <Text style={styles.viewAllText}>{t('home.posts.viewAll')}</Text>
        <DirectionalIcon name="chevron-forward" size={14} color={colors.primary as string} />
      </TouchableOpacity>
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  card: {
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.lg,
    borderWidth: 1,
    borderColor: colors.border,
    paddingHorizontal: spacing.md,
    paddingTop: spacing.xs,
  },
  empty: { alignItems: 'center', gap: spacing.xs, paddingVertical: spacing.lg },
  emptyText: { fontSize: typography.fontSize.sm, color: colors.text.secondary },
  row: { alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.sm },
  divider: { borderTopWidth: 1, borderTopColor: colors.border },
  avatar: { width: 32, height: 32, borderRadius: 16 },
  avatarFallback: { alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(24,119,242,0.1)' },
  body: { flex: 1, minWidth: 0, gap: 2 },
  meta: { fontSize: typography.fontSize.xs, color: colors.text.secondary },
  text: { fontSize: typography.fontSize.sm, color: colors.text.primary },
  stats: { alignItems: 'center', gap: 3, marginTop: 2 },
  stat: { fontSize: typography.fontSize.xs, color: colors.text.secondary, marginEnd: 6 },
  thumb: { width: 48, height: 48, borderRadius: 8, backgroundColor: colors.background.secondary },
  videoThumb: { alignItems: 'center', justifyContent: 'center', backgroundColor: '#111827' },
  viewAll: { alignItems: 'center', justifyContent: 'center', gap: 4, paddingVertical: spacing.sm, borderTopWidth: 1, borderTopColor: colors.border },
  viewAllText: { fontSize: typography.fontSize.sm, color: colors.primary, fontFamily: typography.fontFamily.semibold },
});
